import './ProjectCompanies.css';
import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../supabase';
import ProjectModal from './ProjectModal';
import ProjectDetailModal from './ProjectDetailModal';
import ExportButtons from './ExportButtons';
import ImportModal from './ImportModal';

function statusBadge(status) {
  const map = { Active: 'badge-active', Completed: 'badge-completed', Pending: 'badge-pending', Rejected: 'badge-rejected', Disputed: 'badge-disputed' };
  return <span className={`badge ${map[status] || ''}`}>{status}</span>;
}

const fmtNum = n => n != null ? `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 0 })}` : '—';

function SortTh({ label, sortKey, sortState, onSort }) {
  const [key, dir] = sortState;
  const active = key === sortKey;
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => onSort(sortKey)}>
      {label} <span style={{ color: active ? 'var(--accent)' : 'var(--text3)', fontSize: 10 }}>{active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  );
}

function getPending(fin) {
  if (!fin) return 0;
  return Math.max(0, (fin.agreed_fee || 0) - (fin.paid_amount || 0));
}

function getStatus(p) { return p.status; }


function sortKey(p) {
  if (p.order_date) return p.order_date;
  const match = p.project_code?.match(/(\d{2})(\d+)$/);
  if (match) return `20${match[1]}-01-01`;
  return '1970-01-01';
}

export default function ProjectsPage({ projects, customers, onRefresh, user }) {
  const canPublish=['owner','operations','business_development'].includes(user?.app_metadata?.role);
  const [publications,setPublications]=useState([]),[publicationFilter,setPublicationFilter]=useState(''),[publicationError,setPublicationError]=useState(''),[publicationsLoading,setPublicationsLoading]=useState(true);
  useEffect(()=>{if(!canPublish){setPublicationsLoading(false);return;}let active=true;
  const load=async()=>{try{if(user?.app_metadata?.role==='owner'){
   const responses=await Promise.all(['/api/project-social','/api/project-website','/api/publication-center/context'].map(url=>fetch(url,{credentials:'same-origin'})));
   if(responses.some(r=>!r.ok))throw Error('load');const [s,w,c]=await Promise.all(responses.map(r=>r.json()));
   const rows=c.folders.flatMap(f=>{const j=s.jobs.find(j=>j.folder_job_id===f.id),v=w.jobs.find(v=>v.social_job_id===j?.id);return [{id:j?.id||f.id,project_id:f.project_id,additional_project_ids:(f.reports||[]).map(r=>r.id),status:j?.state||'preparing',channel:'LinkedIn'},...(v?[{id:v.id,project_id:f.project_id,additional_project_ids:(f.reports||[]).map(r=>r.id),status:v.state,channel:'Web'}]:[])];});
   if(active)setPublications(rows);
  }else{const {data,error}=await supabase.from('publications').select('id,project_id,additional_project_ids,status');if(error)throw error;if(active)setPublications(data||[]);}
  if(active)setPublicationError('');}catch{if(active)setPublicationError('Yayın bilgileri yüklenemedi. Sayfayı yenile.');}finally{if(active)setPublicationsLoading(false);}};load();return()=>{active=false;};},[canPublish,user?.app_metadata?.role]);
  const labels={candidate:'Geçmiş',preparing:'Hazırlanıyor',queued:'Sırada',pending_review:'Onay bekliyor',review:'Onay bekliyor',ready:'Geçmiş',published:'Yayınlandı',archived:'Geçmiş',waiting_sources:'Kaynak bekliyor',revision_requested:'Revize',approved:'Yayın sırasında',publishing:'Yayınlanıyor',uncertain:'Doğrulanmalı',blocked:'Bekletildi'};
  const linked=useMemo(()=>{const map={};publications.forEach(r=>[...new Set([r.project_id,...(r.additional_project_ids||[])].filter(Boolean))].forEach(id=>(map[id]??=[]).push(r)));return map;},[publications]);
  const publicationSummary=id=>{const rows=linked[id]||[];if(!rows.length)return '—';return rows.map(r=>(r.channel?r.channel+': ':'')+(labels[r.status]||r.status)).join(' · ');};
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [showRejected, setShowRejected] = useState(false);
  const canEdit=['owner','operations'].includes(user?.app_metadata?.role);
  const [modal, setModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [importing, setImporting] = useState(false);
  const [sort, setSort] = useState(['date', 'desc']);

  const services = [...new Set(projects.map(p => p.service_type).filter(Boolean))].sort();

  const handleSort = (key) => {
    setSort(([k, d]) => k === key ? [key, d === 'asc' ? 'desc' : 'asc'] : [key, 'asc']);
  };

  const enriched = useMemo(() => projects.map(p => ({ ...p, _status: getStatus(p), _pending: getPending(p.financials?.[0]) })), [projects]);

  const filtered = useMemo(() => {
    let list = enriched.filter(p => {
      if (!showRejected && p._status === 'Rejected') return false;
      const cust = customers.find(c => c.id === p.customer_id);
      const q = search.toLowerCase();
      const match = `${p.project_code} ${p.project_group_code||''} ${(p.legacy_project_codes||[]).join(' ')} ${p.report_title||''} ${p.project_name} ${p.council} ${cust?.full_name || ''} ${cust?.company || ''}`.toLowerCase().includes(q);
      const statusMatch = !statusFilter || p._status === statusFilter;
      const serviceMatch = !serviceFilter || p.service_type === serviceFilter;
      const publicationMatch=!publicationFilter||(publicationFilter==='none'?!(linked[p.id]||[]).length:(linked[p.id]||[]).some(r=>r.status===publicationFilter));
      return match && statusMatch && serviceMatch && publicationMatch;
    });

    const [key, dir] = sort;
    list.sort((a, b) => {
      let av, bv;
      if (key === 'date') { av = sortKey(a); bv = sortKey(b); }
      else if (key === 'order_date') { av = a.order_date || ''; bv = b.order_date || ''; }
      else if (key === 'delivery_date') { av = a.delivery_date || ''; bv = b.delivery_date || ''; }
      else if (key === 'project_code') { av = a.project_code || ''; bv = b.project_code || ''; }
      else if (key === 'agreed_fee') { av = a.financials?.[0]?.agreed_fee || 0; bv = b.financials?.[0]?.agreed_fee || 0; }
      else if (key === 'pending') { av = a._pending; bv = b._pending; }
      else if (key === 'status') { av = a._status || ''; bv = b._status || ''; }
      else if (key === 'company') { av=customers.find(c=>c.id===a.customer_id)?.company||'';bv=customers.find(c=>c.id===b.customer_id)?.company||''; }
      else if (key === 'customer') {
        const ca = customers.find(c => c.id === a.customer_id);
        const cb = customers.find(c => c.id === b.customer_id);
        av = ca?.full_name || ''; bv = cb?.full_name || '';
      } else { av = a[key] || ''; bv = b[key] || ''; }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [enriched, customers, search, statusFilter, serviceFilter, showRejected, sort, publicationFilter, linked]);

  const grouped=[];for(const p of filtered){let g=grouped.find(g=>g.id===(p.project_group_id||p.id));if(!g){g={id:p.project_group_id||p.id,code:p.project_group_code||p.project_code,name:p.project_name,reports:[]};grouped.push(g);}g.reports.push(p);}const display=grouped.flatMap(g=>g.reports.length>1?[{_group:g},...g.reports.sort((a,b)=>a.report_index-b.report_index)]:g.reports);
  const rejectedCount = enriched.filter(p => p._status === 'Rejected').length;

  const [deleteError,setDeleteError]=useState('');
  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project? Publication records will be kept.')) return;
    setDeleting(id);
    setDeleteError('');
    try {
      const current=projects.find(p=>p.id===id);
      const {data,error}=await supabase.from('projects').delete().eq('id',id).eq('version',current?.version).select('id');
      if(error)throw error;
      if(!data?.length)throw new Error('Proje değişmiş veya silme yetkin yok. Sayfayı yenileyip tekrar dene.');
      onRefresh();
    }catch(e){setDeleteError(e.code==='23503'?'Bu projeye bağlı çalışma, ödeme veya başka kayıtlar var. Bu kayıtlar korunuyor; proje silinemedi.':e.message||'Proje silinemedi.');}
    finally{setDeleting(null);}
  };

  const custName = (id) => { const c = customers.find(c => c.id === id); return c ? c.full_name : '—'; };

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>Projects</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13 }}>{grouped.length} projects · {filtered.length} reports shown</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canEdit&&<button className="btn-ghost" style={{ fontSize: 12, padding: '8px 14px' }} onClick={() => setImporting(true)}>↑ Import</button>}
          <ExportButtons
            rows={filtered}
            columns={[
              { label: 'Code', get: p => p.project_code },
              { label: 'Project', get: p => p.project_name },
              { label: 'Customer', get: p => { const c = customers.find(c => c.id === p.customer_id); return c ? c.full_name : ''; } },
              { label: 'Company', get: p => { const c = customers.find(c => c.id === p.customer_id); return c ? c.company : ''; } },
              { label: 'Service', get: p => p.service_type },
              { label: 'Council', get: p => p.council },
              { label: 'Planning Ref', get: p => p.planning_ref },
              { label: 'Status', get: p => p._status },
              ...(canEdit ? [{ label: 'Tender Fee', get: p => p.financials?.[0]?.tender_fee },
              { label: 'Agreed Fee', get: p => p.financials?.[0]?.agreed_fee },
              { label: 'Paid', get: p => p.financials?.[0]?.paid_amount },
              { label: 'Pending', get: p => p._pending },
              { label: 'Invoice 1', get: p => p.financials?.[0]?.invoice_no_1 },
              { label: 'Invoice 2', get: p => p.financials?.[0]?.invoice_no_2 }] : []),
              { label: 'Order Date', get: p => p.order_date },
              { label: 'Delivery Date', get: p => p.delivery_date },
              { label: 'Added By', get: p => p.created_by_name },
            ]}
            title="Projects"
            filename="liongate_projects"
          />
          {(canEdit||user?.app_metadata?.create_projects)&&<button className="btn-primary" onClick={() => setModal('new')}>+ New Project</button>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
          <span className="icon">⌕</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by code, name, council, customer..." />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 150 }}>
          <option value="">All statuses</option>
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Completed">Completed</option>
          <option value="Disputed">Disputed</option>
        </select>
        <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)} style={{ width: 150 }}>
          <option value="">All services</option>
          {services.map(s => <option key={s}>{s}</option>)}
        </select>
        {canPublish&&<select aria-label="Yayın durumu" value={publicationFilter} onChange={e=>setPublicationFilter(e.target.value)} style={{width:160}}><option value="">Tüm yayın durumları</option><option value="none">Yayın yok</option>{Object.entries(labels).map(([v,l])=><option key={v} value={v}>{l}</option>)}</select>}
        <button className={showRejected ? 'btn-primary' : 'btn-ghost'} style={{ fontSize: 12 }} onClick={() => setShowRejected(v => !v)}>
          {showRejected ? '✓ ' : ''}Rejected ({rejectedCount})
        </button>
      </div>

      {deleteError&&<p role="alert" className="ops-error">{deleteError}</p>}
      {publicationError&&<p role="alert">{publicationError}</p>}
      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="icon">📋</div>
            <p>No projects found</p>
            <span>Try adjusting filters or create a new project</span>
          </div>
        ) : (
          <table className={"table compact-register projects-table projects-company-register"+(canEdit?" projects-with-fee":" projects-readonly")+(canPublish?" projects-with-publications":"")}>
            <thead>
              <tr>
                <SortTh label="Code" sortKey="project_code" sortState={sort} onSort={handleSort} />
                <SortTh label="Project" sortKey="project_name" sortState={sort} onSort={handleSort} />
                <SortTh label="Contact" sortKey="customer" sortState={sort} onSort={handleSort} />
                <SortTh label="Company" sortKey="company" sortState={sort} onSort={handleSort} />
                <SortTh label="Council" sortKey="council" sortState={sort} onSort={handleSort} />
                <th>Service</th>
                
                <SortTh label="Status" sortKey="status" sortState={sort} onSort={handleSort} />
                
                
                {canPublish&&<th>Yayın</th>}
                {canEdit&&<SortTh label="Project fee" sortKey="agreed_fee" sortState={sort} onSort={handleSort} />}
                {canEdit&&<SortTh label="Pending" sortKey="pending" sortState={sort} onSort={handleSort} />}
                
                
                <th></th>
              </tr>
            </thead>
            <tbody>
              {display.map(p => {
                if(p._group){const g=p._group;return <tr key={'group-'+g.id} style={{background:'var(--bg3)'}}><td><strong>{g.code}</strong></td><td colSpan={canEdit?(canPublish?9:8):(canPublish?7:6)}><strong>{g.name}</strong> <span style={{color:'var(--text3)',marginLeft:12}}>{g.reports.length} reports · Shared folder & announcement</span></td><td>{(canEdit||user?.app_metadata?.create_projects)&&<button className="btn-ghost" onClick={()=>setModal({newReport:true,group:g})}>+ Report</button>}</td></tr>;}

                const fin = p.financials?.[0];
                const pending = p._pending;
                return (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setDetailModal(p)}>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{p.project_code}</span></td>
                    <td className="td-main" style={{ maxWidth: 200 }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{grouped.find(g=>g.id===(p.project_group_id||p.id))?.reports.length>1?(p.report_title||p.service_type):p.project_name}</div>
                      {p.planning_ref && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{p.planning_ref}</div>}
                    </td>
                    <td style={{ maxWidth: 140 }}><div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{custName(p.customer_id)}</div></td>
                    <td style={{maxWidth:180}} title={customers.find(c=>c.id===p.customer_id)?.company||undefined}><div style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{(() => {const name=customers.find(c=>c.id===p.customer_id)?.company?.trim();return !name||/^(individual client|private client.*|tbc)$/i.test(name)?'—':name;})()}</div></td>
                    <td title={p.council || undefined}><div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.council || '—'}</div></td>
                    <td><span style={{ fontSize: 12, background: 'var(--bg3)', padding: '2px 8px', borderRadius: 4 }}>{p.service_type}</span></td>
                    
                    <td>{statusBadge(p._status)}</td>
                    
                    
                    {canPublish&&<td onClick={e=>e.stopPropagation()}><button className="project-publication" disabled={publicationsLoading||!!publicationError} title={publicationSummary(p.id)==='—'?'Yayın akışını aç':publicationSummary(p.id)} onClick={()=>window.dispatchEvent(new CustomEvent('publication:project',{detail:p.id}))}>{publicationsLoading?'…':publicationError?'Yüklenemedi':publicationSummary(p.id)}</button></td>}
                    {canEdit&&<td>{fmtNum(fin?.agreed_fee)}</td>}
                    {canEdit&&<td style={{ color: pending > 0 ? 'var(--red)' : 'var(--text3)' }}>{fmtNum(pending)}</td>}
                    
                    
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {(canEdit||user?.app_metadata?.create_projects)&&<button className="btn-ghost" title="Add a report to this project" onClick={()=>setModal({newReport:true,group:{id:p.project_group_id,code:p.project_group_code,name:p.project_name,reports:[p]}})}>+ Report</button>}{canEdit&&<button className="btn-ghost" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => setModal(p)}>Edit</button>}
                        {canEdit&&<button className="btn-danger" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => deleteProject(p.id)} disabled={deleting === p.id}>Del</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {detailModal && (
        <ProjectDetailModal project={detailModal} customers={customers} onClose={() => setDetailModal(null)} onRefresh={() => { setDetailModal(null); onRefresh(); }} user={user} />
      )}
      {importing && <ImportModal type="projects" onClose={() => setImporting(false)} onDone={() => { setImporting(false); onRefresh(); }} />}
      {modal && (
        <ProjectModal initialValues={modal?.newReport?{project_group_id:modal.group.id,project_name:modal.group.name,customer_id:modal.group.reports[0].customer_id,drive_url:modal.group.reports[0].drive_url,order_date:modal.group.reports[0].order_date}:undefined} project={modal === 'new'||modal?.newReport ? null : modal} customers={customers} onClose={() => setModal(null)} onSaved={() => { setModal(null); onRefresh(); }} user={user} />
      )}
    </div>
  );
}
